"use client";

import { FadeIn } from "@/components/ui/motion";
import { AppWindow, Cloud, Database, Server, Terminal } from "lucide-react";

const techCategories = [
    {
        name: "Frontend",
        icon: AppWindow,
        stack: ["React", "Next.js", "TypeScript", "TailwindCSS", "Framer Motion", "Three.js", "Flutter", "React Native", "Svelte", "Astro", "Vite"]
    },
    {
        name: "Backend",
        icon: Server,
        stack: ["Node.js", "Python", "Go (Golang)", "Rust", "FastAPI", "Express", "Django", "GraphQL", "Bun", "Deno", "NestJS", "Prisma", "Strapi", "Payload CMS"]
    },
    {
        name: "Data & AI",
        icon: Database,
        stack: ["PostgreSQL", "MongoDB", "Redis", "Vector DBs", "OpenAI API", "PyTorch", "LangChain", "HuggingFace", "Gemini API", "Claude"]
    },
    {
        name: "DevOps & Cloud",
        icon: Cloud,
        stack: ["AWS", "Google Cloud", "Vercel", "Docker", "Kubernetes", "Terraform", "GitHub Actions", "Prometheus", "Supabase", "Firebase", "Fly.io"]
    }
];

function getIconUrl(tech: string): string {
    const map: Record<string, string> = {
        "React": "react",
        "Next.js": "next-js",
        "TypeScript": "typescript",
        "TailwindCSS": "tailwindcss",
        "Framer Motion": "framer-motion",
        "Three.js": "three-js",
        "Flutter": "flutter",
        "React Native": "react",
        "Svelte": "svelte",
        "Astro": "astro",
        "Vite": "vite",
        "Node.js": "node-js",
        "Python": "python",
        "Go (Golang)": "go-golang",
        "Rust": "rust",
        "FastAPI": "fastapi",
        "Express": "express",
        "Django": "django",
        "GraphQL": "graphql",
        "Bun": "bun",
        "Deno": "deno",
        "NestJS": "nestjs",
        "Prisma": "prisma",
        "Strapi": "strapi",
        "Payload CMS": "payload-cms",
        "PostgreSQL": "postgresql",
        "MongoDB": "mongodb",
        "Redis": "redis",
        "Vector DBs": "vector-dbs",
        "OpenAI API": "openai-api",
        "PyTorch": "pytorch",
        "LangChain": "langchain",
        "HuggingFace": "huggingface",
        "Gemini API": "gemini-api",
        "Claude": "claude",
        "AWS": "aws",
        "Google Cloud": "google-cloud",
        "Vercel": "vercel",
        "Docker": "docker",
        "Kubernetes": "kubernetes",
        "Terraform": "terraform",
        "GitHub Actions": "github-actions",
        "Prometheus": "prometheus",
        "Supabase": "supabase",
        "Firebase": "firebase",
        "Fly.io": "fly-io"
    };
    const slug = map[tech];
    return slug ? `/logos/${slug}.svg` : "";
}

export default function Technologies() {
    return (
        <section className="py-20 bg-transparent">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <FadeIn>
                    <div className="mb-16">
                        <div className="flex items-center gap-3 mb-4">
                            <Terminal className="w-6 h-6 text-gray-400" />
                            <h2 className="text-3xl font-bold tracking-tight">The Modern Toolbelt</h2>
                        </div>
                        <p className="text-gray-500 max-w-2xl text-lg font-medium leading-relaxed">
                            We don&apos;t just follow trends; we choose the right tool for the job. Our stack is built for performance, scalability, and developer experience.
                        </p>
                    </div>
                </FadeIn>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {techCategories.map((cat, i) => (
                        <FadeIn key={i} delay={i * 0.1}>
                            <div className="bg-white/30 border border-zinc-200/50 backdrop-blur-lg p-8 rounded-[32px] shadow-sm space-y-6">
                                <div className="flex items-center gap-3 border-b border-zinc-200/40 pb-3">
                                    <div className="p-2 rounded-xl bg-gray-50/50 border border-zinc-200/40 text-black">
                                        <cat.icon className="w-5 h-5" />
                                    </div>
                                    <h3 className="font-bold text-xl text-gray-900">{cat.name}</h3>
                                </div>
                                <div className="flex flex-wrap gap-2.5">
                                    {cat.stack.map((tech, j) => {
                                        const iconUrl = getIconUrl(tech);
                                        return (
                                            <span
                                                key={j}
                                                className="px-4.5 py-2.5 bg-white/40 border border-zinc-200/40 rounded-xl text-sm font-semibold text-gray-600 hover:text-black hover:bg-white hover:border-zinc-300 transition-all duration-200 cursor-default shadow-sm flex items-center gap-2"
                                            >
                                                {iconUrl && (
                                                    <img
                                                        src={iconUrl}
                                                        alt={tech}
                                                        className="w-4.5 h-4.5 object-contain"
                                                    />
                                                )}
                                                <span>{tech}</span>
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                        </FadeIn>
                    ))}
                </div>
            </div>
        </section>
    );
}
